import React, { useState } from 'react';
import {
  Box,
  Tabs,
  Tab,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Menu as MenuIcon,
  School as SchoolIcon,
  Psychology as PsychologyIcon,
  Quiz as QuizIcon,
  Insights as InsightsIcon,
  Map as MapIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import MindMapViewer from './MindMapViewer';
import LearningAssessment from './LearningAssessment';
import ResourceRecommendationEngine from './ResourceRecommendationEngine';
import AdaptiveQuiz from './AdaptiveQuiz';
import LearningDashboard from './LearningDashboard';
import LearningDemo from './LearningDemo';
import { 
  LearningStyleProfile, 
  LearningPath, 
  LearningResource, 
  MindMapNode,
  Quiz,
  LearningAnalytics
} from '../../types/learning';
import { ancientAliensLearningMap, learningAchievements, sampleLearningPath, sampleLearningStyleProfile } from '../../data/ancientAliensLearningData';

interface LearningSystemProps {
  onClose?: () => void;
}

const LearningSystem: React.FC<LearningSystemProps> = ({ onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeTab, setActiveTab] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showAssessment, setShowAssessment] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [showDemo, setShowDemo] = useState(true);
  const [currentQuiz, setCurrentQuiz] = useState<Quiz | null>(null);
  const [selectedNode, setSelectedNode] = useState<MindMapNode | null>(null);
  const [learningStyle, setLearningStyle] = useState<LearningStyleProfile>(sampleLearningStyleProfile);
  const [learningPath, setLearningPath] = useState<LearningPath>(sampleLearningPath);
  const [userProgress, setUserProgress] = useState<Record<string, number>>(sampleLearningPath.progress);

  // Generate sample analytics
  const analytics: LearningAnalytics = {
    userId: 'current-user',
    totalTimeSpent: learningPath.totalTimeSpent,
    nodesCompleted: Object.values(userProgress).filter(progress => progress >= 0.8).length,
    totalNodes: ancientAliensLearningMap.nodes.length,
    averageMasteryLevel: Object.values(userProgress).reduce((sum, progress) => sum + progress, 0) / Object.keys(userProgress).length,
    learningStyleProfile: learningStyle,
    strengths: ['Visual Learning', 'Critical Thinking', 'Research Skills'],
    weaknesses: ['Mathematical Concepts', 'Technical Writing'],
    recommendedFocusAreas: ['Archaeological Evidence', 'Scientific Methodology'],
    learningVelocity: 2.5,
    retentionRate: 0.85,
    engagementScore: 0.78
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  const handleNodeClick = (node: MindMapNode) => {
    setSelectedNode(node);
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  const handleResourceClick = (resource: LearningResource) => {
    // In a real implementation, this would open the resource
    console.log('Opening resource:', resource.title);
  };

  const handleAssessmentComplete = (profile: LearningStyleProfile, responses: any[]) => {
    setLearningStyle(profile);
    setShowAssessment(false);
    // Update learning path with new profile
    setLearningPath(prev => ({
      ...prev,
      learningStyleProfile: profile
    }));
  };

  const handleQuizComplete = (score: number, responses: any[]) => {
    // Update progress based on quiz performance
    if (selectedNode) {
      const newProgress = Math.min(1, (userProgress[selectedNode.id] || 0) + (score * 0.3));
      setUserProgress(prev => ({
        ...prev,
        [selectedNode.id]: newProgress
      }));
    }
    setShowQuiz(false);
    setCurrentQuiz(null);
  };

  const generateQuiz = (node: MindMapNode): Quiz => {
    const nodeQuestions = ancientAliensLearningMap.assessments.filter(q => q.nodeId === node.id);
    
    return {
      id: `quiz-${node.id}`,
      title: `${node.title} Knowledge Check`,
      nodeId: node.id,
      questions: nodeQuestions,
      timeLimit: 10, // 10 minutes
      passingScore: 0.7,
      learningStyle: learningStyle,
      difficultyLevel: node.difficultyLevel
    };
  };

  const handleStartQuiz = () => {
    if (selectedNode) {
      const quiz = generateQuiz(selectedNode);
      setCurrentQuiz(quiz);
      setShowQuiz(true);
    }
  };

  const renderContent = () => {
    if (showDemo) {
      return (
        <LearningDemo onStartLearning={() => setShowDemo(false)} />
      );
    }

    if (showAssessment) {
      return (
        <LearningAssessment
          questions={ancientAliensLearningMap.assessments}
          onComplete={handleAssessmentComplete}
          onCancel={() => setShowAssessment(false)}
        />
      );
    }

    if (showQuiz && currentQuiz) {
      return (
        <AdaptiveQuiz
          quiz={currentQuiz}
          learningStyle={learningStyle}
          onComplete={handleQuizComplete}
          onCancel={() => setShowQuiz(false)}
        />
      );
    }

    switch (activeTab) {
      case 0: // Mind Map
        return (
          <Box sx={{ height: '100%', width: '100%' }}>
            <MindMapViewer
              nodes={ancientAliensLearningMap.nodes}
              resources={ancientAliensLearningMap.resources}
              learningStyle={learningStyle}
              userProgress={userProgress}
              onNodeClick={handleNodeClick}
              onResourceClick={handleResourceClick}
            />
          </Box>
        );

      case 1: // Resources
        return (
          <Box sx={{ p: 3 }}>
            <ResourceRecommendationEngine
              resources={ancientAliensLearningMap.resources}
              currentNode={selectedNode}
              learningStyle={learningStyle}
              userProgress={userProgress}
              learningPath={learningPath}
              onResourceSelect={handleResourceClick}
            />
          </Box>
        );

      case 2: // Dashboard
        return (
          <LearningDashboard
            analytics={analytics}
            learningPath={learningPath}
            nodes={ancientAliensLearningMap.nodes}
            achievements={learningAchievements}
            onNodeClick={handleNodeClick}
            onStartAssessment={() => setShowAssessment(true)}
          />
        );

      default:
        return null;
    }
  };

  const drawerContent = (
    <Box sx={{ width: 250 }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">Learning Menu</Typography>
        <IconButton onClick={() => setDrawerOpen(false)}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      <List>
        <ListItem 
          button 
          selected={activeTab === 0}
          onClick={() => handleTabChange({} as React.SyntheticEvent, 0)}
        >
          <ListItemIcon>
            <MapIcon />
          </ListItemIcon>
          <ListItemText primary="Mind Map" />
        </ListItem>
        <ListItem 
          button 
          selected={activeTab === 1}
          onClick={() => handleTabChange({} as React.SyntheticEvent, 1)}
        >
          <ListItemIcon>
            <SchoolIcon />
          </ListItemIcon>
          <ListItemText primary="Resources" />
        </ListItem>
        <ListItem 
          button 
          selected={activeTab === 2}
          onClick={() => handleTabChange({} as React.SyntheticEvent, 2)}
        >
          <ListItemIcon>
            <InsightsIcon />
          </ListItemIcon>
          <ListItemText primary="Dashboard" />
        </ListItem>
        <Divider sx={{ my: 1 }} />
        <ListItem button onClick={() => setShowAssessment(true)}>
          <ListItemIcon>
            <PsychologyIcon />
          </ListItemIcon>
          <ListItemText primary="Learning Assessment" />
        </ListItem>
        {selectedNode && (
          <ListItem button onClick={handleStartQuiz}>
            <ListItemIcon>
              <QuizIcon />
            </ListItemIcon>
            <ListItemText primary="Take Quiz" />
          </ListItem>
        )}
      </List>
    </Box>
  );

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <AppBar position="static" elevation={1}>
        <Toolbar>
          {isMobile && (
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => setDrawerOpen(true)}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Ancient Aliens Learning System
          </Typography>

          {!isMobile && (
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{ minHeight: 64 }}
            >
              <Tab 
                icon={<MapIcon />} 
                label="Mind Map" 
                iconPosition="start"
                sx={{ minHeight: 64 }}
              />
              <Tab 
                icon={<SchoolIcon />} 
                label="Resources" 
                iconPosition="start"
                sx={{ minHeight: 64 }}
              />
              <Tab 
                icon={<InsightsIcon />} 
                label="Dashboard" 
                iconPosition="start"
                sx={{ minHeight: 64 }}
              />
            </Tabs>
          )}

          {onClose && (
            <IconButton color="inherit" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* Content */}
      <Box sx={{ flexGrow: 1, overflow: 'hidden', height: 'calc(100vh - 64px)' }}>
        {renderContent()}
      </Box>

      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
      >
        {drawerContent}
      </Drawer>
    </Box>
  );
};

export default LearningSystem;
